import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { T } from "react-translator-component";
import { useNavigate } from "react-router-dom";
import { i18nValue } from "../../functions";
import { HotelContext } from "../../context/hotel";
import { UserContext } from "../../context/user";
import MenuCommerceList from "./MenuCommerceList";
import MenuCommerceCart from "./MenuCommerceCart";
import useMenuCommerce from "./useMenuCommerce";
import "./menuCommerce.css";

const MenuCommerceModule = ({ block, language }) => {
  const navigate = useNavigate();
  const { currency, getUrlPath } = useContext(HotelContext);
  const { data: userData } = useContext(UserContext);
  const [submissionState, setSubmissionState] = useState("idle");
  const [createdRequestId, setCreatedRequestId] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(3);
  const defaultDeliveryPointCode =
    block.defaultDeliveryPointCode !== undefined ? block.defaultDeliveryPointCode : block.deliveryPoint || "";

  const {
    menu,
    loading,
    error,
    quantities,
    cart,
    summary,
    setSummary,
    observations,
    setObservations,
    deliveryPoint,
    setDeliveryPoint,
    isDeliveryPointMissing,
    sending,
    searchTerm,
    setSearchTerm,
    activeCategoryIndex,
    setActiveCategoryIndex,
    cartItemsCustomizations,
    missingCustomizations,
    hasCustomizableItems,
    hasMissingCustomizations,
    addItem,
    removeItem,
    updateCustomization,
    resetCart,
    openSummary,
    closeSummary,
    submitRequest,
    filterItems,
  } = useMenuCommerce({
    menuId: block.menuId,
    requestType: block.requestType,
    language,
    askForDeliveryPoint: block.askForDeliveryPoint !== false,
    deliveryPointEditable: block.deliveryPointEditable !== false,
    defaultDeliveryPointCode,
  });

  const title = i18nValue(block, "title", language);
  const description = i18nValue(block, "description", language);
  const submitLabel = i18nValue(block, "submitLabel", language);
  const showBuyButton = block.showBuyButton !== false;
  const itemAppearance = {
    cardLayout: block.cardLayout || "horizontal",
    titleFontSize: block.titleFontSize || "medium",
    descriptionFontSize: block.descriptionFontSize || "medium",
    priceFontSize: block.priceFontSize || "medium",
  };
  const isPublicRequestFlow = !userData?.CheckIn?.id && !userData?.RoomOccupation?.id;
  const requestDetailPath = useMemo(() => {
    if (!createdRequestId) {
      return null;
    }

    return getUrlPath(
      isPublicRequestFlow ? `/public/requests/${createdRequestId}` : `/requests/${createdRequestId}`
    );
  }, [createdRequestId, getUrlPath, isPublicRequestFlow]);

  const handleCloseSummary = useCallback(() => {
    setSubmissionState("idle");
    setCreatedRequestId(null);
    setRedirectCountdown(3);
    closeSummary();
  }, [closeSummary]);

  const handleImmediateRedirect = useCallback(() => {
    if (!requestDetailPath) {
      return;
    }

    handleCloseSummary();
    resetCart();
    navigate(requestDetailPath, { replace: true });
  }, [handleCloseSummary, navigate, requestDetailPath, resetCart]);

  const handleSubmitRequest = useCallback(async () => {
    setSubmissionState("submitting");

    const response = await submitRequest();

    if (!response) {
      setSubmissionState("idle");
      return null;
    }

    const nextRequestId =
      response?.id || response?.data?.id || response?.request?.id || response?.data?.request?.id || null;

    if (!nextRequestId) {
      handleCloseSummary();
      resetCart();
      return response;
    }

    setCreatedRequestId(nextRequestId);
    setRedirectCountdown(3);
    setSubmissionState("success");
    return response;
  }, [handleCloseSummary, resetCart, submitRequest]);

  useEffect(() => {
    if (submissionState !== "success" || !requestDetailPath) {
      return undefined;
    }

    if (redirectCountdown <= 0) {
      handleImmediateRedirect();
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setRedirectCountdown((current) => current - 1);
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [handleImmediateRedirect, redirectCountdown, requestDetailPath, submissionState]);

  if (!block?.menuId) {
    return (
      <Alert variant="light" style={{ borderRadius: 18, border: "1px dashed #D9DCE6", color: "#7E8299" }}>
        {T("menu.items_not_found")}
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="loading text-center mt-4 mb-4">
        <div className="spinner-border" role="status"></div>
      </div>
    );
  }

  if (error || !menu) {
    return (
      <Alert variant="danger" style={{ borderRadius: 18 }}>
        {error === "empty" ? T("menu.items_not_found") : T("menu.fail_laod_items")}
      </Alert>
    );
  }

  return (
    <div
      className="menu-commerce-module"
      style={{
        marginBottom: Number(block.spacingBottom || 24),
        borderRadius: Number(block.borderRadius || 20),
        background: block.backgroundColor || "#FFFFFF",
        border: "1px solid rgba(130,55,138,0.08)",
        boxShadow: "0 16px 30px rgba(15,23,42,0.06)",
      }}
    >
      {title ? (
        <h4 className="menu-commerce-title" style={{ color: block.textColor || "#181C32" }}>
          {title}
        </h4>
      ) : null}

      {description ? (
        <div className="menu-commerce-description" style={{ color: "#7E8299" }}>{description}</div>
      ) : null}

      <MenuCommerceList
        menu={menu}
        language={language}
        currency={currency}
        quantities={quantities}
        itemAppearance={itemAppearance}
        addItem={addItem}
        removeItem={removeItem}
        filterItems={filterItems}
        showSearch={block.showSearch !== false}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        showCategories={block.showCategories !== false}
        activeCategoryIndex={activeCategoryIndex}
        setActiveCategoryIndex={setActiveCategoryIndex}
        showBuyButton={showBuyButton}
      />

      {showBuyButton ? <MenuCommerceCart
        cart={cart}
        currency={currency}
        summary={summary}
        setSummary={setSummary}
        observations={observations}
        setObservations={setObservations}
        deliveryPoint={deliveryPoint}
        setDeliveryPoint={setDeliveryPoint}
        askForDeliveryPoint={block.askForDeliveryPoint !== false}
        deliveryPointEditable={block.deliveryPointEditable !== false}
        defaultDeliveryPointCode={defaultDeliveryPointCode}
        isDeliveryPointMissing={isDeliveryPointMissing}
        cartItemsCustomizations={cartItemsCustomizations}
        language={language}
        addItem={addItem}
        removeItem={removeItem}
        updateCustomization={updateCustomization}
        resetCart={resetCart}
        openSummary={openSummary}
        closeSummary={handleCloseSummary}
        submitRequest={handleSubmitRequest}
        sending={sending}
        submissionState={submissionState}
        redirectCountdown={redirectCountdown}
        onImmediateRedirect={handleImmediateRedirect}
        submitLabel={submitLabel}
        missingCustomizations={missingCustomizations}
        hasCustomizableItems={hasCustomizableItems}
        hasMissingCustomizations={hasMissingCustomizations}
        showCartSummary={block.showCartSummary !== false}
      /> : null}
    </div>
  );
};

export default MenuCommerceModule;
